import React, { useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';

export interface FireworksOptions {
  count?: number;
  x?: number;
  y?: number;
}

export const triggerDiwaliFireworks = (options?: FireworksOptions) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('launch-diwali-fireworks', { detail: options || {} })
    );
  }
};

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  alpha: number;
  decay: number;
  size: number;
  flicker: boolean;
  trail: { x: number; y: number }[];
}

interface Rocket {
  x: number;
  y: number;
  targetY: number;
  vx: number;
  vy: number;
  color: string;
  trail: { x: number; y: number }[];
}

const FESTIVE_COLORS = [
  '#f59e0b', // Festive Gold
  '#ef4444', // Red / Kumkum
  '#fbbf24', // Marigold Yellow
  '#ea580c', // Bright Orange
  '#10b981', // Emerald Green
  '#ec4899', // Gulabi Pink
  '#38bdf8', // Electric Cyan
  '#a855f7', // Royal Purple
];

export const DiwaliFireworksCanvas: React.FC<{ ambient?: boolean }> = ({
  ambient = true,
}) => {
  const { isDiwali } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const rocketsRef = useRef<Rocket[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const lastAmbientTimeRef = useRef<number>(Date.now());

  const createExplosion = useCallback((x: number, y: number, color: string, particleCount = 55) => {
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + (Math.random() - 0.5) * 0.3;
      const speed = Math.random() * 5.5 + 1.5;
      const particleColor =
        Math.random() > 0.4
          ? color
          : FESTIVE_COLORS[Math.floor(Math.random() * FESTIVE_COLORS.length)];

      particlesRef.current.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color: particleColor,
        alpha: 1,
        decay: Math.random() * 0.015 + 0.012,
        size: Math.random() * 2.5 + 1.2,
        flicker: Math.random() > 0.3,
        trail: [],
      });
    }

    // Add extra golden glittering sparkler stardust embers
    for (let j = 0; j < 20; j++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 3 + 0.5;
      particlesRef.current.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color: '#fbbf24',
        alpha: 1,
        decay: Math.random() * 0.01 + 0.008,
        size: Math.random() * 1.8 + 0.8,
        flicker: true,
        trail: [],
      });
    }
  }, []);

  const launchRocket = useCallback(
    (startX?: number, targetY?: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const width = canvas.width;
      const height = canvas.height;

      const x = startX ?? Math.random() * (width * 0.7) + width * 0.15;
      const tY = targetY ?? Math.random() * (height * 0.45) + height * 0.1;
      const color = FESTIVE_COLORS[Math.floor(Math.random() * FESTIVE_COLORS.length)];
      const vy = -(Math.random() * 3.5 + 9.5);
      const vx = (Math.random() - 0.5) * 2;

      rocketsRef.current.push({
        x,
        y: height - 10,
        targetY: tY,
        vx,
        vy,
        color,
        trail: [],
      });
    },
    []
  );

  // Trigger burst handler
  const handleLaunchBurst = useCallback(
    (count = 3, targetX?: number, targetY?: number) => {
      for (let i = 0; i < count; i++) {
        setTimeout(() => {
          const x = targetX ? targetX + (Math.random() - 0.5) * 120 : undefined;
          launchRocket(x, targetY);
        }, i * 240);
      }
    },
    [launchRocket]
  );

  useEffect(() => {
    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent<FireworksOptions>;
      const count = customEvent.detail?.count ?? 4;
      handleLaunchBurst(count, customEvent.detail?.x, customEvent.detail?.y);
    };

    window.addEventListener('launch-diwali-fireworks', handleCustomEvent);
    return () => window.removeEventListener('launch-diwali-fireworks', handleCustomEvent);
  }, [handleLaunchBurst]);

  useEffect(() => {
    if (!isDiwali) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleResize = () => {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    // Initial celebratory launch on theme activate
    handleLaunchBurst(3);

    let isRunning = true;

    const render = () => {
      if (!isRunning) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Ambient fireworks: launch occasional gentle rocket every 8-12 seconds
      if (ambient && rocketsRef.current.length === 0 && particlesRef.current.length === 0) {
        const now = Date.now();
        if (now - lastAmbientTimeRef.current > 9000) {
          lastAmbientTimeRef.current = now;
          launchRocket();
        }
      }

      // Update & Draw Rockets
      for (let i = rocketsRef.current.length - 1; i >= 0; i--) {
        const r = rocketsRef.current[i];
        r.trail.push({ x: r.x, y: r.y });
        if (r.trail.length > 5) r.trail.shift();

        r.x += r.vx;
        r.y += r.vy;
        r.vy += 0.08; // slight gravity

        // Draw rocket tail
        ctx.strokeStyle = r.color;
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        if (r.trail.length > 0) {
          ctx.moveTo(r.trail[0].x, r.trail[0].y);
          for (let t = 1; t < r.trail.length; t++) {
            ctx.lineTo(r.trail[t].x, r.trail[t].y);
          }
          ctx.lineTo(r.x, r.y);
          ctx.stroke();
        }

        // Draw glowing rocket tip
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(r.x, r.y, 2, 0, Math.PI * 2);
        ctx.fill();

        // Explode condition
        if (r.y <= r.targetY || r.vy >= -1) {
          createExplosion(r.x, r.y, r.color);
          rocketsRef.current.splice(i, 1);
        }
      }

      // Update & Draw Particles (Explosion Spiders & Sparkles)
      for (let i = particlesRef.current.length - 1; i >= 0; i--) {
        const p = particlesRef.current[i];
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > 3) p.trail.shift();

        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.06; // gravity
        p.vx *= 0.98; // friction
        p.vy *= 0.98;
        p.alpha -= p.decay;

        if (p.alpha <= 0) {
          particlesRef.current.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.globalAlpha = Math.max(0, p.alpha);

        // Draw particle tail
        if (p.trail.length > 0) {
          ctx.strokeStyle = p.color;
          ctx.lineWidth = p.size * 0.8;
          ctx.beginPath();
          ctx.moveTo(p.trail[0].x, p.trail[0].y);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
        }

        // Particle head with sparkle
        ctx.fillStyle = p.flicker && Math.random() > 0.4 ? '#ffffff' : p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);

    const handleVisibility = () => {
      if (document.hidden) {
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      } else {
        animFrameRef.current = requestAnimationFrame(render);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      isRunning = false;
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isDiwali, ambient, handleLaunchBurst, launchRocket, createExplosion]);

  if (!isDiwali) return null;

  return (
    <canvas
      ref={canvasRef}
      className="ns-diwali-fireworks-canvas"
      aria-hidden="true"
      data-testid="diwali-fireworks-canvas"
    />
  );
};

export default DiwaliFireworksCanvas;

